
/**
 * Objective Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useObjectiveStore } from '../../store/objective.store.js';
import { useObjectiveRelatedTreatmentsStore } from '../../store/objective-related-treatments.store.js';
import { useKpiStore } from '../../store/kpi.store.js';
import { useStrategicInitiativeStore } from '../../store/strategic-initiative.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ObjectiveIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Quiet list for selectors (DependencySelectorDialog). Does not update the list store.
     */
    async listObjectives(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || response.meta || {},
                meta: response.meta || response.pagination || {},
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage, data: [] };
        }
    }

    /**
     * Fetch items and update store
     */
    async fetchObjectives(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useObjectiveStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;

            store.setObjectives(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || pagination.totalItems || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return {
                success: true,
                data: data || [],
                pagination: pagination || {},
                meta: pagination || {},
                uniqueValues: uniqueValues || {},
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchObjectiveById(id) {
        const store = useObjectiveStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedObjective(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createObjective(data) {
        const store = useObjectiveStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('iso27001Objective:messages.createSuccess', { defaultValue: 'Objective created successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateObjective(id, data) {
        const store = useObjectiveStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('iso27001Objective:messages.updateSuccess', { defaultValue: 'Objective updated successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteObjective(id) {
        const store = useObjectiveStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('iso27001Objective:messages.deleteSuccess', { defaultValue: 'Objective deleted successfully' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete
     */
    async batchDeleteObjective(ids) {
        const store = useObjectiveStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.batchDelete(ids);
            toast.success(i18next.t('iso27001Objective:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: `${ids.length} objectives deleted` }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get related risks for objective
     */
    async getRelatedRisks(id, params) {
        try {
            return await this.service.getRelatedRisks(id, params);
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Fetch linked Treatments (Paginated)
     */
    async fetchTreatments(id, params = {}) {
        const store = useObjectiveRelatedTreatmentsStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedTreatments(id, currentParams);

            store.setData?.(response.data || []);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Link risks to objective
     */
    async addRisks(id, riskIds) {
        try {
            const response = await this.service.addRisks(id, riskIds);
            toast.success(i18next.t('integrationToasts:risksLinkedToObjective', { defaultValue: 'Risks linked to objective' }));
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Link treatments to objective
     */
    async addTreatments(id, treatmentIds) {
        try {
            const response = await this.service.addTreatments(id, treatmentIds);
            toast.success(i18next.t('integrationToasts:actionsLinkedToObjective', { defaultValue: 'Actions linked to objective' }));
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Unlink treatment from objective
     */
    async removeTreatment(id, treatmentId, opts = {}) {
        try {
            const response = await this.service.removeTreatment(id, treatmentId);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.treatmentUnlinked'));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Unlink risk from objective
     */
    async removeRisk(id, riskId, opts = {}) {
        try {
            const response = await this.service.removeRisk(id, riskId);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.riskUnlinked'));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Fetch linked KPIs (Paginated)
     */
    async fetchKpis(id, params = {}) {
        const store = useKpiStore.getState();
        store.setLoading?.(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 5 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedKpis(id, currentParams);

            if (store.setData) {
                store.setData(response.data || []);
            } else if (store.setKpis) {
                store.setKpis(response.data || []);
            }

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 5);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            if (store.setPagination) {
                store.setPagination({
                    currentPage,
                    itemsPerPage,
                    totalItems,
                    totalPages,
                });
            }

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Link KPIs to objective
     */
    async addKpis(id, kpiIds, opts = {}) {
        try {
            const response = await this.service.addKpis(id, kpiIds);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.kpisLinked'));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Unlink KPI from objective
     */
    async removeKpi(id, kpiId, opts = {}) {
        try {
            const response = await this.service.removeKpi(id, kpiId);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.kpiUnlinked', { defaultValue: 'KPI unlinked from objective' }));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Fetch linked Strategic Initiatives (Paginated)
     */
    async fetchInitiatives(id, params = {}) {
        const store = useStrategicInitiativeStore.getState();
        store.setLoading?.(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedInitiatives(id, currentParams);

            if (store.setData) {
                store.setData(response.data || []);
            } else if (store.setInitiatives) {
                store.setInitiatives(response.data || []);
            }

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            if (store.setPagination) {
                store.setPagination({
                    currentPage,
                    itemsPerPage,
                    totalItems,
                    totalPages,
                });
            }

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Link Strategic Initiatives to objective
     */
    async addInitiatives(id, initiativeIds, opts = {}) {
        try {
            const response = await this.service.addInitiatives(id, initiativeIds);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.initiativesLinked'));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Unlink Strategic Initiative from objective
     */
    async removeInitiative(id, initiativeId, opts = {}) {
        try {
            const response = await this.service.removeInitiative(id, initiativeId);
            if (!opts.silent) {
                toast.success(i18next.t('iso27001Objective:messages.initiativeUnlinked', { defaultValue: 'Strategic initiative unlinked from objective' }));
            }
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.silent) toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
